// 도입문의 수기 탭의 날짜 곁 칸(2026-10-23) — 오너: "주차는 일자에 맞는 주차 자동 기입되게 해주고, 요일도 동일".
// 사본 시트의 셈: 주는 월요일부터, 그 달 1일이 든 주가 1주차(26.10: 10.4 일 → 1주차, 10.5 월 → 2주차,
// 10.21 수 → 4주차). 요일은 한 글자('월').

import { kstTodayYmd, kstYmdHyphen } from '@/lib/format-date';

const DAYS = '일월화수목금토';

/**
 * 날짜 값('2026-10-21')을 그 열에 이미 보이는 모양처럼 — 시트에 쓰이기 전 잠깐 보여 줄 글자.
 * sample: 같은 열의 보이는 글자 하나('10월6일' · '10. 6' · '2026. 10. 6' · '10/6'). 없으면 '10. 21'.
 */
export function dateLike(iso: string, sample?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [y, mo, d] = [m[1]!, Number(m[2]), Number(m[3])];
  const s = sample?.trim() ?? '';
  if (/월.*일/.test(s)) return `${mo}월${d}일`;
  if (/^\d{4}\s*[.\-/]/.test(s)) return `${y}. ${mo}. ${d}`;
  if (/^\d{1,2}\/\d{1,2}$/.test(s)) return `${mo}/${d}`;
  if (/^\d{1,2}\.\d{1,2}$/.test(s)) return `${mo}.${d}`;
  return `${mo}. ${d}`;
}

/**
 * 날짜처럼 쓴 글자('10.12' · '10. 12' · '10월12일' · '10/12' · '2026. 10. 12') → 'YYYY-MM-DD' · 아니면 ''.
 * 시트에 글자로 들어 있는 날짜 칸(시연 예정 · 시연 완료)을 달력에서 그 날로 열려고. 해가 없으면 year
 * (기본은 한국 날짜의 올해).
 */
export function isoFromText(text: string, year = Number(kstTodayYmd().slice(0, 4))): string {
  const t = text.trim();
  const full = /^(\d{4})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{1,2})\.?$/.exec(t);
  const short = /^(\d{1,2})\s*(?:[./]|월)\s*(\d{1,2})\s*일?\.?$/.exec(t);
  const [y, mo, d] = full
    ? [Number(full[1]), Number(full[2]), Number(full[3])]
    : short
      ? [year, Number(short[1]), Number(short[2])]
      : [0, 0, 0];
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return '';
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' → { 주차: '4', 요일: '수' } · 날짜가 아니면 null */
export function weekAndDay(iso: string): { week: string; day: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  const lead = (new Date(d.getFullYear(), d.getMonth(), 1).getDay() + 6) % 7; // 1일 앞의 월~ 빈 날
  return {
    week: String(Math.floor((d.getDate() - 1 + lead) / 7) + 1),
    day: DAYS[d.getDay()]!,
  };
}

/**
 * 처음 열 달 탭 — 오늘(한국 날짜)이 든 달, 없으면 가장 최근 달(months 는 최근 달 먼저).
 * 다음 달 탭은 사람이 미리 복제해 비워 두므로(2026-10-23 '자동 IB(26.11)' · '(2026_11)') 맨 앞 탭이 아직 빈
 * 다음 달일 수 있다.
 */
export function currentMonthKey<K>(
  months: readonly { key: K; month: string }[],
  today = new Date(),
): K | null {
  const now = kstYmdHyphen(today)?.slice(0, 7);
  return (months.find((m) => m.month === now) ?? months[0])?.key ?? null;
}
